import type { ResearchArea, TimelineRecord } from "../types";

export interface NavigationItem {
  label: string;
  href: string;
  children?: NavigationItem[];
}

export interface ProfessorProfile {
  name: string;
  nameEn: string;
  title: string;
  appointment: string;
  introduction: string;
  education: TimelineRecord[];
  career: TimelineRecord[];
  email: string;
  telephone: string;
  office: string;
  officialProfileUrl: string;
  verifiedAt: string;
}

export interface ResearchFact {
  label: string;
  value: string;
}

export interface ResearchEvidence {
  id: "push" | "weather" | "peer" | "ott" | "xr";
  title: string;
  journal: string;
  year: number;
  doi: string;
  facts: ResearchFact[];
  findings: string[];
  reuseNote: string;
}

export interface MediaCredit {
  id: string;
  path: string;
  alt: string;
  author?: string;
  license?: string;
  sourceUrl: string;
  permissionNote?: string;
}

export const site = {
  name: "최정혜 교수 연구실",
  institution: "연세대학교",
  school: "경영대학",
  field: "마케팅",
  email: "jeonghye@yonsei.ac.kr",
  telephone: "+82 2 2123 6575",
  office: "서울특별시 서대문구 연세로 50, 연세대학교 경영관 537호",
  sourceDate: "2026-07-23",
} as const;

export const navigation: NavigationItem[] = [
  { label: "Home", href: "/" },
  { label: "Research", href: "/research" },
  { label: "Publications", href: "/publications" },
  {
    label: "People",
    href: "/people",
    children: [
      { label: "Members", href: "/people" },
      { label: "Alumni", href: "/alumni" },
    ],
  },
  {
    label: "Activities",
    href: "/news",
    children: [
      { label: "Awards", href: "/news" },
      { label: "Research Support", href: "/projects" },
    ],
  },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

export const professor: ProfessorProfile = {
  name: "최정혜",
  nameEn: "Jeonghye Choi",
  title: "연세대학교 경영대학 마케팅 교수",
  appointment: "언더우드 특훈교수 · 2021–현재",
  introduction:
    "펜실베이니아대학교 와튼스쿨에서 마케팅 박사학위를 받았습니다. 2010년 연세대학교 경영대학에 부임해 2015년 부교수, 2020년 교수로 승진했습니다.",
  education: [
    {
      year: "Ph.D.",
      title: "University of Pennsylvania, The Wharton School",
      detail: "Marketing",
    },
    { year: "M.S.", title: "KAIST", detail: "석사" },
    { year: "B.S.", title: "KAIST", detail: "학사" },
  ],
  career: [
    {
      year: "2010–2015",
      title: "연세대학교 경영대학 조교수",
    },
    {
      year: "2015–2020",
      title: "연세대학교 경영대학 부교수",
    },
    {
      year: "2020–현재",
      title: "연세대학교 경영대학 교수",
    },
    {
      year: "2021–현재",
      title: "연세대학교 언더우드 특훈교수",
    },
  ],
  email: "jeonghye@yonsei.ac.kr",
  telephone: "02-2123-6575",
  office: "서울특별시 서대문구 연세로 50, 연세대학교 경영관 537호",
  officialProfileUrl:
    "https://ysb.yonsei.ac.kr/faculty.asp?mid=m02&sOpt=&uid=92",
  verifiedAt: "2026-07-23",
};

export const researchAreas: ResearchArea[] = [
  {
    id: "personalization",
    label: "Personalization",
    title: "개인화 메시지와 모바일 참여",
    summary:
      "실제 서비스에서 전달되는 푸시 알림의 개인화 수준이 이용자 참여에 미치는 영향을 현장실험으로 살펴봅니다.",
    method: "신체활동 앱 이용자 595명 대상 현장실험",
    publicationId: "10.1177/14413582251356702",
    figure: "push",
  },
  {
    id: "context",
    label: "Physical Context",
    title: "공간·날씨와 모바일 참여",
    summary:
      "반려동물 앱 이용자의 활동 공간과 지역별 날씨를 연결해 앱 사용 빈도와 세션 지속시간을 분석합니다.",
    method: "산책·돌봄·동반 공간과 기상 조건 분석",
    publicationId: "10.1108/JRIM-04-2025-0234",
    figure: "weather",
  },
  {
    id: "platforms",
    label: "Social Influence",
    title: "플랫폼과 사회적 영향",
    summary:
      "온라인 게임에서 서로 알지 못하는 이용자의 구매가 다른 이용자의 선택에 미치는 영향을 실제 구매 기록으로 분석합니다.",
    method: "대규모 온라인 게임 구매 기록 분석",
    publicationId: "10.1093/jcr/ucae075",
    figure: "peer",
  },
  {
    id: "content",
    label: "Digital Content",
    title: "콘텐츠·OTT와 게임 행동",
    summary:
      "무료·광고형과 유료·구독형 OTT를 구분해 소셜미디어 이용과 서비스 접속·시청의 관계를 분석합니다.",
    method: "모바일 앱·웹 패널 이용자 3,975명 분석",
    publicationId: "10.1016/j.jbusres.2026.116256",
    figure: "ott",
  },
  {
    id: "immersive",
    label: "Immersive Technology",
    title: "몰입 기술과 글로벌 브랜드",
    summary:
      "확장현실 경험의 설계, 브랜드의 현지성·새로움과 플랫폼 맥락이 소비자 참여에 미치는 영향을 분석합니다.",
    method: "뷰티 브랜드 257개 · 브랜드-일 375,477건",
    publicationId: "10.1057/s41267-024-00756-w",
    figure: "xr",
  },
];

export const researchEvidence: ResearchEvidence[] = [
  {
    id: "push",
    title:
      "Push the Paw: A Field Experiment on Personalised Push Notifications and User Engagement",
    journal: "Australasian Marketing Journal",
    year: 2026,
    doi: "10.1177/14413582251356702",
    facts: [
      { label: "Data", value: "595 physical activity app users" },
      { label: "Method", value: "Field experiment" },
    ],
    findings: [
      "개인화 알림이 일반 알림보다 참여를 높였습니다.",
      "보호자와 반려동물 이름을 함께 쓴 조건이 반려동물 이름만 쓴 조건보다 효과가 컸습니다.",
      "이전 활동 정보가 개인화 효과를 강화했습니다.",
    ],
    reuseNote: "자료: 논문 초록·공개 연구정보 · 자체 도식.",
  },
  {
    id: "weather",
    title:
      "Weathering the digital shift: How interaction spaces and weather conditions shape mobile app engagement",
    journal: "Journal of Research in Interactive Marketing",
    year: 2026,
    doi: "10.1108/JRIM-04-2025-0234",
    facts: [
      { label: "Model", value: "Interaction spaces + Weather conditions" },
      { label: "Outcomes", value: "Usage frequency + Session duration" },
    ],
    findings: [
      "기능 공간에서 사용 빈도가 27.8% 증가했습니다.",
      "호우 때 사용 빈도는 6.2%, 세션 지속시간은 12.7% 감소했습니다.",
      "산책 공간과 동반 가능 공간에서 세션 지속시간은 각각 6.6%, 4.3% 증가했습니다.",
      "폭염 때 세션 지속시간은 5.2% 감소했고, 강풍의 효과는 확인되지 않았습니다.",
    ],
    reuseNote: "Li, Choi & Kim (2026), Figure 1 재작도 · CC BY 4.0.",
  },
  {
    id: "peer",
    title:
      "In the Company of Strangers: Social Influence from Anonymous Peers in Online Game Settings",
    journal: "Journal of Consumer Research",
    year: 2025,
    doi: "10.1093/jcr/ucae075",
    facts: [
      { label: "Context", value: "Online game purchases" },
      { label: "Mechanism", value: "Informational social influence" },
    ],
    findings: [
      "익명 또래의 구매가 이용자의 구매에 영향을 미쳤습니다.",
      "경쟁보다 정보 단서로 작동했고, 경험이 적은 이용자에게서 영향이 더 컸습니다.",
    ],
    reuseNote: "자료: 논문 초록·공개 연구정보 · 자체 도식.",
  },
  {
    id: "ott",
    title:
      "Free versus paid over-the-top video streaming services and the influence of social media",
    journal: "Journal of Business Research",
    year: 2026,
    doi: "10.1016/j.jbusres.2026.116256",
    facts: [
      { label: "Data", value: "3,975 mobile app and web panel users" },
      { label: "Services", value: "Free/ad-supported + Paid/subscription" },
    ],
    findings: [
      "소셜미디어 이용은 OTT 접속과 시청 모두에 보완적으로 작용했습니다.",
      "접속과의 관계는 무료·광고형에서, 시청과의 관계는 유료·구독형에서 더 강했습니다.",
      "본인의 이용 경험은 관계를 약화했고, 또래 이용은 무료 서비스에서만 관계를 강화했습니다.",
    ],
    reuseNote: "자료: 논문 초록·공개 연구정보 · 자체 도식.",
  },
  {
    id: "xr",
    title:
      "Liability of foreignness in immersive technologies: Evidence from extended reality innovations",
    journal: "Journal of International Business Studies",
    year: 2025,
    doi: "10.1057/s41267-024-00756-w",
    facts: [
      { label: "Brands", value: "257 beauty brands in South Korea" },
      { label: "Panel", value: "375,477 brand-day observations · 2019–2022" },
    ],
    findings: [
      "외국 브랜드는 확장현실 혁신에서 불리했습니다.",
      "현실감이 낮고 상호작용성과 생생함이 높은 설계는 불리함을 키웠습니다.",
      "새로운 브랜드와 플랫폼 활용은 불리함을 완화했습니다.",
    ],
    reuseNote: "자료: 논문 초록·공개 연구정보 · 자체 도식.",
  },
];

export const mediaCredits: MediaCredit[] = [
  {
    id: "professor",
    path: "/media/choi-jeonghye.jpg",
    alt: "최정혜 연세대학교 경영대학 교수 공식 프로필 사진",
    sourceUrl:
      "https://ysb.yonsei.ac.kr/faculty.asp?mid=m02&sOpt=&uid=92",
    permissionNote: "검토용 시안에 사용하며 최종 공개 전 사용 승인을 확인합니다.",
  },
  {
    id: "campus",
    path: "/media/yonsei-campus-wide.jpg",
    alt: "연세대학교 캠퍼스 전경",
    author: "Christian Bolz",
    license: "CC BY-SA 4.0",
    sourceUrl:
      "https://commons.wikimedia.org/wiki/File:Yonsei_University_campus.jpg",
  },
  {
    id: "baegyang",
    path: "/media/yonsei-baekyang.jpg",
    alt: "연세대학교 백양로",
    author: "Striker9498",
    license: "CC BY-SA 4.0",
    sourceUrl:
      "https://commons.wikimedia.org/wiki/File:20230624_%EC%97%B0%EC%84%B8%EB%8C%80%ED%95%99%EA%B5%90_%EB%B0%B1%EC%96%91%EB%A1%9C.jpg",
  },
];
